import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import Highlight from 'react-highlighter';
import moment from 'moment';

const messages = defineMessages({
  NO_COMPANY_FOUND: { id: 'CompanyList.NO_COMPANY_FOUND', defaultMessage: 'Es wurden keine Unternehmen gefunden.' },
  USER: { id: 'CompanyList.USER', defaultMessage: 'Benutzer' },
  CUSTOMER: { id: 'CompanyList.CUSTOMER', defaultMessage: 'Kunden' },
  OPEN_CUSTOMERS: { id: 'CompanyList.OPEN_CUSTOMERS', defaultMessage: 'Kundenübersicht öffnen' },
  CREATED: { id: 'CompanyList.CREATED', defaultMessage: 'Hinzugefügt am' },
  COMPANY: { id: 'CompanyList.COMPANY', defaultMessage: 'Unternehmen' },
  ACTIONS: { id: 'CompanyList.ACTIONS', defaultMessage: 'Aktionen' },
  MAINTAIN_USER: { id: 'CompanyList.MAINTAIN_USER', defaultMessage: 'Benutzer verwalten' },
  ADJUST_PROFILE: { id: 'CompanyList.ADJUST_PROFILE', defaultMessage: 'Profil anpassen' },
  BILLING_RECORD: { id: 'CompanyList.BILLING_RECORD', defaultMessage: 'Einzelverbindungsnachweis' },
});

class CompanyList extends Component {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('CompanyListTableState_v2');
    const defaultState = {
      sortColumn: 'name',
      sortDirection: 'asc',
      columnWidths: {
        col0: 300, // Unternehmen
        col1: 150, // Benutzer
        col2: 150, // Kunden
        col3: 150, // Erstellt
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState) } : defaultState;
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths } = this.state;
    localStorage.setItem('CompanyListTableState_v2', JSON.stringify({ sortColumn, sortDirection, columnWidths }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  render() {
    const { items, filter, intl: { formatMessage } } = this.props;
    
    let filteredItems = items || [];
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filteredItems = filteredItems.filter(company => {
        return (company.name && company.name.toLowerCase().includes(lowerFilter));
      });
    }

    if (!(filteredItems && filteredItems.length)) {
      return <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>{formatMessage(messages.NO_COMPANY_FOUND)}</div>;
    }

    const { sortColumn, sortDirection, columnWidths } = this.state;
    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortColumn) return 0;
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      
      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return sortDirection === 'asc' ? 1 : -1;
      if (valB === undefined || valB === null) return sortDirection === 'asc' ? -1 : 1;
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th onClick={() => this.toggleSort('name')} style={getThStyle('col0')}>
                {formatMessage(messages.COMPANY)} 
                {this.renderSortIcon('name')}
                {renderResizer('col0')}
              </th>
              <th onClick={() => this.toggleSort('user_count')} style={{ ...getThStyle('col1'), textAlign: 'center' }}>
                {formatMessage(messages.USER)}
                {this.renderSortIcon('user_count')}
                {renderResizer('col1')}
              </th>
              <th onClick={() => this.toggleSort('customer_count')} style={{ ...getThStyle('col2'), textAlign: 'center' }}>
                {formatMessage(messages.CUSTOMER)}
                {this.renderSortIcon('customer_count')}
                {renderResizer('col2')}
              </th>
              <th onClick={() => this.toggleSort('created')} style={{ ...getThStyle('col3'), textAlign: 'center' }}>
                {formatMessage(messages.CREATED)}
                {this.renderSortIcon('created')}
                {renderResizer('col3')}
              </th>
              <th style={{ ...thBaseStyle, padding: '16px 24px', width: 'auto', textAlign: 'right', cursor: 'default' }}>
                {formatMessage(messages.ACTIONS)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(company => (
              <tr key={company.id} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
                <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Link to={`/company/${company.id}/`} style={{ fontWeight: 'bold', color: 'var(--text-color)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                    <div style={{ width: '32px', height: '32px', minWidth: '32px', borderRadius: '6px', backgroundColor: 'var(--pill-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                      <i className="fa fa-building" />
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {filter ? <Highlight search={filter}>{company.name}</Highlight> : company.name}
                    </span>
                  </Link>
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', fontWeight: 'bold' }}>
                    {company.user_count}
                  </span>
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', backgroundColor: 'rgba(217, 119, 6, 0.15)', color: '#f59e0b', fontWeight: 'bold' }}>
                    {company.customer_count}
                  </span>
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'center', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <i className="fa fa-calendar" style={{ marginRight: '6px' }} />
                  {company.created ? moment(company.created, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YY') : '-'}
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Link to={`/company/${company.id}/user/`} title={formatMessage(messages.MAINTAIN_USER)} style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--btn-default-bg)', border: '1px solid var(--btn-default-border)', textDecoration: 'none' }}>
                      <i className="fa fa-users" />
                    </Link>
                    <Link to={`/company/${company.id}/settings/`} title={formatMessage(messages.ADJUST_PROFILE)} style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--btn-default-bg)', border: '1px solid var(--btn-default-border)', textDecoration: 'none' }}>
                      <i className="fa fa-cog" />
                    </Link>
                    <Link to={`/company/${company.id}/billing/`} title={formatMessage(messages.BILLING_RECORD)} style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--btn-default-bg)', border: '1px solid var(--btn-default-border)', textDecoration: 'none' }}>
                      <i className="fa fa-file-text-o" />
                    </Link>
                    <Link to={`/company/${company.id}/customer/`} title={formatMessage(messages.OPEN_CUSTOMERS)} style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--btn-default-bg)', border: '1px solid var(--btn-default-border)', textDecoration: 'none' }}>
                      <i className="fa fa-address-book" />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(CompanyList);
